import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { LightTable } from '@/components'
import {
	CheckBoxFormField,
	Form,
	SelectFormField,
	Submit,
} from '@/components/UberForm'
import {
	CsvImportDomainDto,
	CsvImportExistingDomainDto,
	ImportReturnMessage,
} from '@/endpoints/models'
import { useAppContext, useAuthContext } from '@/hooks'

import { ImportStructureProps } from '../../hooks/useImport'
import { useCsvImporterContext } from '../context/CsvImporterContext'
import { useCheckMessages } from '../hooks/useCheckMessages'
import { useCsvImporterColumns } from '../hooks/useCsvImporterColumns'
import { useNamesNormalisations } from '../hooks/useNamesNormalisations'
import { useOnCheckSubmit } from '../hooks/useOnCheckSubmit'
import { StepTitle } from './styled'

const INITIAL_VALUES = {
	caseSensitive: false,
	namesNormalisation: 'NONE',
	generateCodes: false,
	createNewDomains: false,
	importAllColumns: false,
} as ImportStructureProps

export const Check = () => {
	const { t } = useAppContext()
	const auth = useAuthContext()
	const namesNormalisations = useNamesNormalisations()
	const { columns, subColumns } = useCsvImporterColumns()

	const [formValues, setFormValues] =
		useState<ImportStructureProps>(INITIAL_VALUES)

	const handleSubmit = useOnCheckSubmit()

	const {
		state: { checkResult, formDataCheck, rows, onMapped },
	} = useCsvImporterContext()

	const onSubmit = async (values: ImportStructureProps) => {
		await handleSubmit(values)
	}

	const checkErrors = useCheckMessages()

	useEffect(() => {
		if ((checkResult?.errors?.length ?? 0) > 0) {
			return
		}

		if (checkResult && rows) {
			onMapped(
				rows,
				{ ...formValues, importAllColumns: !formValues.importAllColumns },
				true,
			)
		}
	}, [onMapped, checkResult, rows, formValues])

	return (
		<>
			<StepTitle>{t('CHECK')}</StepTitle>
			<Form<ImportStructureProps>
				onSubmit={onSubmit}
				onChange={(values) => {
					setFormValues(values)
				}}
				disabled={!!formDataCheck}
				initialValues={INITIAL_VALUES}
			>
				<Wrapper>
					<CheckboxStyled
						name="caseSensitive"
						title={t('IMPORT_CASE_SENSITIVE')}
					/>
					{auth.canManageLOVs() && (
						<CheckboxStyled
							name="createNewDomains"
							title={t('IMPORT_CREATE_NEW_DOMAINS')}
						/>
					)}
					<CheckboxStyled
						name="importAllColumns"
						title={t('DO_NOT_IMPORT_TECHNICAL_COLUMNS')}
					/>
					<SelectStyled
						name="namesNormalisation"
						title={t('IMPORT_NAMES_NORMALISATION')}
						options={namesNormalisations}
						valueKey="value"
						labelKey="label"
						required={true}
						clearable={false}
					/>
				</Wrapper>
				{!formDataCheck && <SubmitStyled>{t('CHECK')}</SubmitStyled>}
			</Form>
			{checkResult?.errors?.map((value) => (
				<TextError key={value.messageType}>
					{
						checkErrors[
							value.messageType as ImportReturnMessage.MessageTypeEnum
						]
					}
					:&nbsp;
					{value.object}
				</TextError>
			))}
			{checkResult?.warnings
				?.filter(
					(warning) =>
						![
							ImportReturnMessage.MessageTypeEnum
								.TABLE_ALREADY_EXISTS_IN_FOLDER,
							ImportReturnMessage.MessageTypeEnum
								.TABLE_ALREADY_EXISTS_IN_SYSTEM,
						].includes(
							warning.messageType as ImportReturnMessage.MessageTypeEnum,
						),
				)
				.map((value) => (
					<TextWarning key={value.messageType}>
						{
							checkErrors[
								value.messageType as ImportReturnMessage.MessageTypeEnum
							]
						}
						:&nbsp;
						{value.object}
					</TextWarning>
				))}

			{checkResult?.domainDtoList && (
				<>
					<div style={{ margin: '10px 0' }}>{t('DOMAINS')}</div>
					<LightTable<CsvImportDomainDto>
						data={checkResult.domainDtoList}
						columns={columns}
						keyField={'domainName'}
						isRowExpandable={(row) =>
							row.matchedDomain == null && row.similarDomain?.length != 0
						}
						expandedRowComponent={(row) => (
							<LightTable<CsvImportExistingDomainDto>
								data={row.similarDomain ? row.similarDomain : []}
								columns={subColumns}
								keyField={'code'}
							/>
						)}
					/>
				</>
			)}
		</>
	)
}

const Wrapper = styled.div`
	display: flex;
	align-items: flex-end;
`

const CheckboxStyled = styled(CheckBoxFormField)`
	flex: 0 1 auto;
`

const SelectStyled = styled(SelectFormField)`
	width: 225px;
	flex: 0 1 auto;
`

const TextError = styled.div`
	color: ${(props) => props.theme.colors.danger.base};
`

const TextWarning = styled.div`
	color: ${(props) => props.theme.colors.warn.base};
`

const SubmitStyled = styled(Submit)`
	margin-bottom: 10px;
`
